import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, Plus, Edit2, Trash2, Check, X, Palette, RotateCcw
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'sonner';
import api, { getCustomEmojis, updateCustomEmoji, deleteCustomEmoji } from '../lib/api';
import EmojiPicker from '../components/EmojiPicker';
import { EMOJI_OPTIONS } from '../lib/categories';

const COLOR_OPTIONS = [
  '#FF4F00', '#3B82F6', '#10B981', '#8B5CF6', '#EC4899', 
  '#F59E0B', '#EF4444', '#00D68F', '#6366F1', '#A855F7',
  '#F472B6', '#FB923C', '#78716C', '#0EA5E9', '#A8A29E',
  '#84CC16', '#14B8A6', '#F43F5E', '#7C3AED', '#2563EB'
];

export default function CategoriesPage() {
  const navigate = useNavigate();
  const { language, t } = useLanguage();
  
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [customCategories, setCustomCategories] = useState([]);
  const [userEmojis, setUserEmojis] = useState({});
  const [systemEmojis, setSystemEmojis] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingSystemEmoji, setEditingSystemEmoji] = useState(null);
  const [formData, setFormData] = useState({ name: '', icon: '📁', color: '#A8A29E' });

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      setLoading(true);
      const [catRes, emojiRes] = await Promise.all([
        api.get('/categories'),
        getCustomEmojis().catch(() => ({ data: { user_emojis: {}, system_emojis: {} } }))
      ]);
      
      const all = catRes.data.categories || [];
      setCategories(all.filter(c => c.is_default));
      setCustomCategories(all.filter(c => !c.is_default));
      setUserEmojis(emojiRes.data.user_emojis || {});
      setSystemEmojis(emojiRes.data.system_emojis || {});
    } catch (error) {
      toast.error(t('loading_error'));
    } finally {
      setLoading(false);
    }
  };

  // Get the effective emoji for a category (user > system > default)
  const getEffectiveEmoji = (cat) => {
    return userEmojis[cat.id] || systemEmojis[cat.id] || cat.icon;
  };

  // Handle custom emoji update for system category
  const handleSystemEmojiUpdate = async (categoryId, newEmoji) => {
    try {
      await updateCustomEmoji(categoryId, newEmoji);
      setUserEmojis(prev => ({ ...prev, [categoryId]: newEmoji }));
      setEditingSystemEmoji(null);
      toast.success(language === 'fr' ? 'Emoji personnalisé' : language === 'pt' ? 'Emoji personalizado' : 'Emoji customized');
    } catch (error) {
      toast.error('Error updating emoji');
    }
  };

  // Reset to system default
  const handleResetEmoji = async (categoryId) => {
    try {
      await deleteCustomEmoji(categoryId);
      setUserEmojis(prev => {
        const newEmojis = { ...prev };
        delete newEmojis[categoryId];
        return newEmojis;
      });
      toast.success(language === 'fr' ? 'Emoji réinitialisé' : language === 'pt' ? 'Emoji redefinido' : 'Emoji reset');
    } catch (error) {
      toast.error('Error resetting emoji');
    }
  };

  const handleCreate = async () => {
    if (!formData.name.trim()) {
      toast.error(t('name_required'));
      return;
    }

    try {
      await api.post('/categories', formData);
      toast.success(t('category_created'));
      setShowModal(false);
      setFormData({ name: '', icon: '📁', color: '#A8A29E' });
      loadCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  const handleUpdate = async () => {
    if (!editingCategory) return;

    try {
      await api.put(`/categories/${editingCategory.id}`, formData);
      toast.success(t('category_updated'));
      setEditingCategory(null);
      setFormData({ name: '', icon: '📁', color: '#A8A29E' });
      loadCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  const handleDelete = async (categoryId) => {
    if (!confirm(t('delete_category_question'))) {
      return;
    }

    try {
      await api.delete(`/categories/${categoryId}`);
      toast.success(t('category_deleted'));
      loadCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  const openEditModal = (category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      icon: category.icon,
      color: category.color
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-24 md:pb-8">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={() => navigate('/settings')}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="font-bold text-lg">
                  {t('categories')}
                </h1>
                <p className="text-sm text-muted-foreground">
                  {t('manage_expense_categories')}
                </p>
              </div>
            </div>
            <Button onClick={() => setShowModal(true)}>
              <Plus className="w-4 h-4 mr-2" />
              {t('new_label')}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Custom Categories */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">
                {t('my_categories')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {customCategories.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {customCategories.map((cat) => (
                    <div
                      key={cat.id}
                      className="relative group p-4 rounded-xl border border-border bg-card hover:shadow-md transition-all"
                    >
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl"
                          style={{ backgroundColor: `${cat.color}20` }}
                        >
                          {cat.icon}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{cat.name}</p>
                          <div 
                            className="w-4 h-4 rounded-full mt-1"
                            style={{ backgroundColor: cat.color }}
                          />
                        </div>
                      </div>
                      
                      {/* Actions */}
                      <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                        <button
                          onClick={() => openEditModal(cat)}
                          className="p-1.5 rounded-lg bg-muted hover:bg-muted/80"
                        >
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => handleDelete(cat.id)}
                          className="p-1.5 rounded-lg bg-red-100 hover:bg-red-200 text-red-600"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-3">
                    <Plus className="w-8 h-8 text-muted-foreground" />
                  </div>
                  <p className="font-medium">
                    {t('no_custom_categories')}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {t('create_categories_to_organize')}
                  </p>
                  <Button className="mt-4" onClick={() => setShowModal(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    {t('create_category')}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Default Categories - Now customizable emojis */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center justify-between">
                <span>{t('default_categories')}</span>
                <span className="text-xs font-normal text-muted-foreground">
                  {language === 'fr' ? 'Cliquez sur un emoji pour le personnaliser' : 
                   language === 'pt' ? 'Clique num emoji para personalizar' : 
                   'Click an emoji to customize'}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                {categories.map((cat) => (
                  <div
                    key={cat.id}
                    className="p-3 rounded-xl border border-border bg-muted/30 hover:border-primary/50 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <div className="relative group">
                        <button
                          onClick={() => setEditingSystemEmoji(cat)}
                          className="w-10 h-10 rounded-lg flex items-center justify-center text-xl hover:scale-110 transition-transform cursor-pointer"
                          style={{ backgroundColor: `${cat.color}20` }}
                          title={language === 'fr' ? 'Modifier l\'emoji' : language === 'pt' ? 'Editar emoji' : 'Edit emoji'}
                        >
                          {getEffectiveEmoji(cat)}
                        </button>
                        {userEmojis[cat.id] && (
                          <button
                            onClick={(e) => { e.stopPropagation(); handleResetEmoji(cat.id); }}
                            className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                            title={language === 'fr' ? 'Réinitialiser' : language === 'pt' ? 'Redefinir' : 'Reset'}
                          >
                            <X className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">
                          {language === 'fr' ? cat.name : language === 'pt' ? (cat.name_pt || cat.name_en) : cat.name_en}
                        </p>
                        {userEmojis[cat.id] && (
                          <p className="text-xs text-primary">
                            {language === 'fr' ? 'Personnalisé' : language === 'pt' ? 'Personalizado' : 'Custom'}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* System Emoji Edit Dialog */}
        <Dialog open={!!editingSystemEmoji} onOpenChange={() => setEditingSystemEmoji(null)}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>
                {language === 'fr' ? 'Personnaliser l\'emoji' : language === 'pt' ? 'Personalizar emoji' : 'Customize emoji'}
              </DialogTitle>
            </DialogHeader>
            {editingSystemEmoji && (
              <div className="space-y-4">
                <div className="text-center">
                  <p className="text-sm text-muted-foreground mb-2">
                    {language === 'fr' ? editingSystemEmoji.name : 
                     language === 'pt' ? (editingSystemEmoji.name_pt || editingSystemEmoji.name_en) : 
                     editingSystemEmoji.name_en}
                  </p>
                  <div className="flex justify-center">
                    <EmojiPicker
                      value={getEffectiveEmoji(editingSystemEmoji)}
                      onChange={(emoji) => handleSystemEmojiUpdate(editingSystemEmoji.id, emoji)}
                    />
                  </div>
                </div>
                {userEmojis[editingSystemEmoji.id] && (
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => { handleResetEmoji(editingSystemEmoji.id); setEditingSystemEmoji(null); }}
                  >
                    <RotateCcw className="w-4 h-4 mr-2" />
                    {language === 'fr' ? 'Réinitialiser par défaut' : language === 'pt' ? 'Redefinir para o padrão' : 'Reset to default'}
                  </Button>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </main>

      {/* Create/Edit Modal */}
      <Dialog open={showModal || !!editingCategory} onOpenChange={() => {
        setShowModal(false);
        setEditingCategory(null);
        setFormData({ name: '', icon: '📁', color: '#A8A29E' });
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingCategory 
                ? t('edit_category')
                : t('new_category')
              }
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Preview */}
            <div className="flex justify-center">
              <div 
                className="w-20 h-20 rounded-2xl flex items-center justify-center text-4xl"
                style={{ backgroundColor: `${formData.color}20` }}
              >
                {formData.icon}
              </div>
            </div>
            
            {/* Name */}
            <div>
              <label className="text-sm font-medium mb-1 block">
                {t('name')}
              </label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder={t('example_category_vacation')}
              />
            </div>
            
            {/* Icon - Using EmojiPicker */}
            <div>
              <label className="text-sm font-medium mb-2 block">
                {t('icon')}
              </label>
              <div className="flex justify-center">
                <EmojiPicker
                  value={formData.icon}
                  onChange={(emoji) => setFormData(prev => ({ ...prev, icon: emoji }))}
                />
              </div>
            </div>
            
            {/* Color */}
            <div>
              <label className="text-sm font-medium mb-2 block flex items-center gap-2">
                <Palette className="w-4 h-4" />
                {t('color')}
              </label>
              <div className="grid grid-cols-10 gap-2">
                {COLOR_OPTIONS.map((color) => (
                  <button
                    key={color}
                    onClick={() => setFormData(prev => ({ ...prev, color }))}
                    className={`w-8 h-8 rounded-full transition-transform hover:scale-110 ${
                      formData.color === color ? 'ring-2 ring-offset-2 ring-primary scale-110' : ''
                    }`}
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowModal(false);
              setEditingCategory(null);
            }}>
              {t('cancel')}
            </Button>
            <Button onClick={editingCategory ? handleUpdate : handleCreate}>
              <Check className="w-4 h-4 mr-2" />
              {editingCategory 
                ? (t('save'))
                : (t('create'))
              }
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
